import { AxiosResponse } from 'axios';
import { getEntry, putEntry } from './db';
import { AxiosResponseOk } from './http-responses';
import { connection } from './internet';
import { session } from './session';


interface CacheEntry {
  keyPath: string;
  accountId: string;
  args: Array<string>;
  result: any;
}

export class OfflineCacheMissError extends Error {
  constructor(objectStore: string, args: unknown[]) {
    super(`No cached data for offline request to ${objectStore} (${JSON.stringify(args)})`);
    this.name = 'OfflineCacheMissError';
  }
}

async function getKeypath(args) {
  if (!session.account) throw new OfflineCacheMissError('session', args);
  return [
    session.account.id,
    ...args,
  ].map(e => e.toString()).join('@');
}

async function cacheResult(objectStore, args, result) {
  if (!session.account) return;
  await putEntry<CacheEntry>(objectStore, {
    keyPath: await getKeypath(args),
    args: args,
    accountId: session.account.id,
    result,
  });
}

async function retrieveCachedEntry(objectStore, args) {
  if (!session.account) return undefined;
  return await getEntry<CacheEntry>(objectStore, await getKeypath(args));
}

async function getRemoteOrLocal<T = any>(
  objectStore: string,
  remoteFn: (...remoteArgs: any[]) => Promise<AxiosResponse<T> | undefined>,
  ...rest: any[]
): Promise<AxiosResponse<T>> {
  // The final argument may be an `onFresh` callback. It opts the caller into
  // cache-first delivery: we serve the cached payload up front, revalidate
  // against the server in the background, and hand the fresh response to this
  // callback so a reactive view can re-render once it arrives. Detected by type,
  // so the plain string/number/array args of every other caller keep working
  // unchanged.
  let onFresh: ((result: AxiosResponse<T>) => void) | undefined;
  let args = rest;
  if (typeof rest[rest.length - 1] === 'function') {
    onFresh = rest[rest.length - 1];
    args = rest.slice(0, -1);
  } else if (rest.length > 0 && rest[rest.length - 1] === undefined) {
    // A caller forwarding an unset onFresh (`…, this.onFresh)` with onFresh
    // undefined). Drop the trailing undefined so it never lands in `args` and
    // corrupts the cache key — getKeypath stringifies every arg.
    args = rest.slice(0, -1);
  }

  // Fetch from the server and persist the payload. Resolves to undefined when
  // we're (or just went) offline — the axios interceptor turns a "Network
  // Error" into undefined — so the caller can fall back to the cache.
  const revalidate = async (): Promise<AxiosResponse<T> | undefined> => {
    if (!connection.isConnected.value) return undefined;
    const result = await remoteFn(...args) as AxiosResponse<T> | undefined;
    if (result) await cacheResult(objectStore, args, result.data);
    return result;
  };

  const cacheEntry = await retrieveCachedEntry(objectStore, args);

  if (cacheEntry && onFresh) {
    // Cache-first, opt-in via onFresh: never block the UI on a request that may
    // be slow or doomed (offline). Serve the persisted payload now, revalidate
    // in the background, and let the reactive view repaint via onFresh when the
    // server responds. Only callers that pass onFresh get this — without a way
    // to repaint, a stale-then-silently-refreshed cache leaves the view one
    // mutation behind (it shows the pre-mutation value and only catches up on
    // the *next* reload), which is the regression this gating prevents.
    revalidate()
      .then(fresh => { if (fresh && onFresh) onFresh(fresh); })
      .catch(() => undefined);
    return new AxiosResponseOk(cacheEntry.result);
  }

  // Network-first (no onFresh, or nothing cached yet): while online, wait for the
  // server so the caller always sees current data — critical right after a
  // mutation. Fall back to the cache only when the request yields nothing
  // (offline or a failed request).
  const fresh = await revalidate();
  if (fresh) return fresh;
  if (cacheEntry) return new AxiosResponseOk(cacheEntry.result);

  // Offline (or a failed request) with no cache to fall back to.
  throw new OfflineCacheMissError(objectStore, args);
}

export { getRemoteOrLocal }
